import { Component, Inject, signal, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ImportService } from '../../../services/import-service';
import { AgGridAngular } from 'ag-grid-angular';
import type { AutoSizeStrategy, ColDef, GetRowIdParams, GridApi, GridReadyEvent, SelectionChangedEvent, ValueGetterParams } from 'ag-grid-community';
import {MatStepper, MatStepperModule} from '@angular/material/stepper';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { PaginatedResponse } from '../../../models/paginated-response';
import { Client } from '../../../models/client';
import { ClientSite } from '../../../models/client-site';
import { MatIconModule } from '@angular/material/icon';
import { LoadingOverlayComponent } from '../../util/loading-overlay-component/loading-overlay-component';
import { ButtonCellRendererComponent } from '../../util/button-cell-renderer-component/button-cell-renderer-component';
import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import { SnackBarService } from '../../../services/snack-bar-service';


@Component({
  selector: 'app-import-sites-from-client-modal-component',
  imports: [AgGridAngular, MatStepperModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatDialogModule, MatIconModule, FormsModule],
  templateUrl: './import-sites-from-client-modal-component.html',
  styleUrl: './import-sites-from-client-modal-component.css',
})
export class ImportSitesFromClientModalComponent {
  private clientSearchGridApi!: GridApi<any>;
  private siteSelectionGridApi!: GridApi<any>;

  public readonly gridContext = {
    componentParent: this
  };

  public readonly gridLocale = AG_GRID_LOCALE_FR;

  public readonly searchResultColumns: ColDef[] = [
    {
      headerName: "ID Ent.",
      field: "id",
      suppressHeaderMenuButton: true,
      width: 75
    },
    { 
      headerName: "Raison sociale", 
      field: "name",
      suppressHeaderMenuButton: true
    },
    {
      headerName: "SIRET",
      field: "legalIdentifier",
      suppressHeaderMenuButton: true,
      width: 100
    },
    { 
      headerName: "", 
      cellRenderer: ButtonCellRendererComponent,
      cellRendererParams: {
        text: 'Sélectionner',
        tooltip: 'Sélectionner ce client',
        onClick: (data: any, index: number) => this.selectClient(data.id),
      },
      suppressHeaderMenuButton: true,
      sortable: false,
      resizable: false,
      maxWidth: 125
    },
  ];
  public readonly searchResultRowData = signal<any[]>([]);

  public readonly siteSelectionColumns: ColDef[] = [
    { 
      headerName: "Nom", 
      field: "name", 
      filter: "agTextColumnFilter", 
      floatingFilter: true
    },
    { 
      headerName: "Adresse", 
      field: "address.line1", 
      filter: "agTextColumnFilter", 
      floatingFilter: true 
    },
    {
      headerName: "Commune", 
      valueGetter: (params: ValueGetterParams) => {
        return `${params.data.address?.city} (${params.data.address?.zipCode})`;
      },
      filter: 'agTextColumnFilter',
      floatingFilter: true,
    },
    {
      headerName: "Code immeuble",
      field: "address.buildingCode",
      filter: false,
      floatingFilter: false
    },
    {
      headerName: "Coordonnées",
      filter: false,
      floatingFilter: false,
      valueGetter: (params: ValueGetterParams) => {
        if (!params.data.address.latitude || !params.data.address.longitude) {
          return '';
        }

        return `${params.data.address.latitude}, ${params.data.address.longitude}`
      }
    }
  ];
  public readonly siteSelectionRowData = signal<any[]>([]);

  public readonly autoSizeStrategy: AutoSizeStrategy = { type: 'fitGridWidth' };
  public readonly loadingOverlayComponent: any = LoadingOverlayComponent;
  public readonly loadingOverlayComponentParams: any = { loadingMessage: 'Chargement des résultats...' };

  @ViewChild('stepper') private readonly stepper!: MatStepper;
  public clientNameQuery!: string;

  public readonly selectedSites = signal<ClientSite[]>([]);
  public readonly isImportingSites = signal<boolean>(false);

  constructor(
    private readonly importService: ImportService,
    private readonly dialogRef: MatDialogRef<ImportSitesFromClientModalComponent>,
    private readonly snackBar: SnackBarService,
    @Inject(MAT_DIALOG_DATA) private readonly data: any
  ) {}


  getRowId(params: GetRowIdParams) {
    return params.data.id;
  }

  onClientSearchGridReady(params: GridReadyEvent): void {
    this.clientSearchGridApi = params.api;
  }

  onClientSearchRowDataUpdated(): void {
    if (!this.clientSearchGridApi) {
      return;
    }

    this.clientSearchGridApi.sizeColumnsToFit();
  }

  onSiteSelectionGridReady(params: GridReadyEvent): void {
    this.siteSelectionGridApi = params.api;
  }

  onSiteSelectionRowDataUpdated(): void {
    if (!this.siteSelectionGridApi) {
      return;
    }

    this.siteSelectionGridApi.sizeColumnsToFit();
  }

  onSiteSelectionChanged(event: SelectionChangedEvent): void {
    this.selectedSites.set(event.api.getSelectedRows());
  }

  ngOnInit(): void {
    this.clientNameQuery = '';
  }

  searchClient(): void {
    this.clientSearchGridApi.setGridOption('loading', true);
    this.importService.searchClient(this.clientNameQuery, 10).subscribe({
      next: (data: PaginatedResponse<Client[]>) => { 
        this.searchResultRowData.set(data.items); 
        this.clientSearchGridApi.setGridOption('loading', false);
      },
      error: () => {
        this.snackBar.error("Erreur rencontrée lors de la recherche du client");
        this.clientSearchGridApi.setGridOption('loading', false);
      }
    });
  }

  selectClient(clientId: number): void {
    this.loadClientLocations(clientId);
    this.stepper.next();
  }

  importSites(): void {
    if (this.selectedSites().length === 0) {
      this.snackBar.warning("Veuillez sélectionner des sites à importer");
      return;
    }

    const sessionCode = this.data.sessionCode;
    if (!sessionCode) {
      this.snackBar.error("Code de session invalide");
      return;
    }

    const siteIds = this.selectedSites().map((site: ClientSite) => { return site.id });
    
    if (sessionCode) {
      this.isImportingSites.set(true);
      this.importService.importSitesFromAdistaLocations(sessionCode, siteIds).subscribe({
        next: (sites) => { 
          this.snackBar.success(`Sites importés avec succès sur la session ${sessionCode}`);
          this.dialogRef.close(sites);
          this.isImportingSites.set(false);
        },
        error: () => { 
          this.snackBar.error("Erreur lors de l'import des sites");
          this.isImportingSites.set(false); 
        }
      });
    }
  }

  loadClientLocations(clientId: number): void {
    this.siteSelectionGridApi.setGridOption('loading', true);
    this.importService.getClientLocations(clientId).subscribe({
      next: (data: PaginatedResponse<ClientSite[]>) => { 
        this.siteSelectionRowData.set(data.items);
        this.siteSelectionGridApi.setGridOption('loading', false); 
      },
      error: () => {
        this.snackBar.error("Erreur lors de la recherche des sites du client");
        this.siteSelectionGridApi.setGridOption('loading', false);
      }
    });
  }

  close(closeValue: boolean): void {
    this.dialogRef.close(closeValue);
  }
}
